import { Box, Card, CardContent, Stack, Typography, alpha, LinearProgress } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { useStableQuery } from "../../hooks/useStableQuery";
import { api } from "../../../../convex/_generated/api";
import { useCycleFilter } from "../../cycleFilterContext";

function formatUsd(n: number, compact = true) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: compact ? "compact" : "standard",
    maximumFractionDigits: compact ? 1 : 2,
  }).format(n);
}

interface BucketProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: string;
  pctOfTotal: number;
  detail?: string;
}

function Bucket({ icon, label, value, color, pctOfTotal, detail }: BucketProps) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: alpha(color, 0.25),
        background: `linear-gradient(135deg, ${alpha(color, 0.12)}, ${alpha(color, 0.03)})`,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, color }}>
        {icon}
        <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
          {label}
        </Typography>
      </Stack>
      <Typography variant="h5" sx={{ fontWeight: 700, color, lineHeight: 1.1 }} title={formatUsd(value, false)}>
        {formatUsd(value)}
      </Typography>
      {detail && (
        <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mt: 0.25 }}>
          {detail}
        </Typography>
      )}
      <LinearProgress
        variant="determinate"
        value={Math.min(100, Math.max(0, pctOfTotal))}
        sx={{
          mt: 1,
          height: 4,
          borderRadius: 2,
          backgroundColor: alpha(color, 0.12),
          "& .MuiLinearProgress-bar": { backgroundColor: color, borderRadius: 2 },
        }}
      />
    </Box>
  );
}

/**
 * MoneyFlowCard — single-glance answer to "where is the money in this cycle?"
 *
 * Four buckets sum |amount| across the operational lifecycle:
 *   - Sent to QBD: posted + cached in qbTransactions
 *   - Auto-flow: bank rows with invoiceRequired=false (Michelle's per-account rules)
 *   - Waiting: matched-not-yet-posted + needs-invoice + pending-review
 *   - Failed: qbImportStatus=failed
 *
 * Each bucket shows its share of cycle total via a thin progress bar so
 * proportions are readable at a glance.
 */
export function MoneyFlowCard() {
  const { cycleArg } = useCycleFilter();
  const flow = useStableQuery(api.dashboard.getMoneyFlow, cycleArg, null);

  if (!flow) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Money flow
          </Typography>
          <LinearProgress sx={{ height: 3, borderRadius: 2 }} />
        </CardContent>
      </Card>
    );
  }

  const waiting = flow.readyToPost + flow.needsInvoice + flow.pendingReview;
  // Lifecycle buckets are mutually exclusive: their sum = cycle total minus
  // `duplicate`. Auto-flow is shown on a separate axis as a class metric
  // (a row can be both "Sent to QBD" and "Auto-flow" — it overlaps).
  const cycleTotal = flow.sentToQbd + waiting + flow.failed + flow.duplicate;
  const pct = (n: number) => (cycleTotal > 0 ? (n / cycleTotal) * 100 : 0);
  const autoFlowPct =
    cycleTotal > 0 ? (flow.autoFlowClass / cycleTotal) * 100 : 0;

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Money flow
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Cycle total: {formatUsd(cycleTotal)}
          </Typography>
        </Stack>

        {/* Lifecycle buckets — exclusive, sum to cycle total */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <Bucket
            icon={<CheckCircleOutlineIcon fontSize="small" />}
            label="Sent to QBD"
            value={flow.sentToQbd}
            pctOfTotal={pct(flow.sentToQbd)}
            color="#55efc4"
            detail={`${pct(flow.sentToQbd).toFixed(0)}% of cycle`}
          />
          <Bucket
            icon={<HourglassEmptyIcon fontSize="small" />}
            label="Waiting"
            value={waiting}
            pctOfTotal={pct(waiting)}
            color="#fdcb6e"
            detail="Match / review / pending"
          />
          <Bucket
            icon={<ReportProblemOutlinedIcon fontSize="small" />}
            label="Failed"
            value={flow.failed}
            pctOfTotal={pct(flow.failed)}
            color="#ff7675"
            detail={flow.failed > 0 ? "QBD rejected" : "Clean run"}
          />
        </Stack>

        {/* Auto-flow CLASS metric — overlay axis, not part of lifecycle sum.
            Counts every row with invoiceRequired=false regardless of where
            it is in the pipeline. */}
        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: alpha("#a29bfe", 0.25),
            background: `linear-gradient(135deg, ${alpha("#a29bfe", 0.10)}, ${alpha(
              "#a29bfe",
              0.02,
            )})`,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={2}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
              <Box sx={{ color: "#a29bfe", display: "flex" }}>
                <AutoAwesomeIcon fontSize="small" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block" }}>
                  Auto-flow class
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Michelle's no-invoice accounts · {autoFlowPct.toFixed(0)}% of cycle
                </Typography>
              </Box>
            </Stack>
            <Box sx={{ flex: 1, textAlign: { xs: "left", sm: "right" } }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: "#a29bfe", lineHeight: 1.1 }}
                title={formatUsd(flow.autoFlowClass, false)}
              >
                {formatUsd(flow.autoFlowClass)}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {formatUsd(flow.autoFlowInQbd)} in QBD · {formatUsd(flow.autoFlowPending)} pending
              </Typography>
            </Box>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={
              flow.autoFlowClass > 0
                ? (flow.autoFlowInQbd / flow.autoFlowClass) * 100
                : 0
            }
            sx={{
              mt: 1.25,
              height: 4,
              borderRadius: 2,
              backgroundColor: alpha("#a29bfe", 0.12),
              "& .MuiLinearProgress-bar": { backgroundColor: "#a29bfe", borderRadius: 2 },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
}
